/**
 * useTeamModule — the Team module composable (Program-Modules epic, W6/W7:
 * the team RETROFIT onto the module framework — the FINAL, largest retirement).
 * Dark behind PROGRAM_MODULES; flag-off keeps today's EXACT team behavior (the
 * existing team activity/dialog untouched, no gate, no runner).
 *
 * The direct analogue of useCoachingModule (Wave 4). Wraps the Nexus
 * `teamModuleState` op behind a thin, typed API and UPGRADES the pre-existing
 * 'team' thin-wrap descriptor (from W2) into a FULL module: panel + policyLoader +
 * nudge. Once registered, the shared trigger runner (useModuleTriggers, already
 * instantiated in TimelineBar) picks team up automatically — the nudge becomes
 * POLICY-DRIVEN, not team-specific code.
 *
 *   - loadTeamModuleState(programId) → server-derived {enabled, gate}
 *   - loadPolicy(programId)          → folds the GATE into a ModulePolicy
 *   - registerDescriptor()           → attaches the hooks onto the 'team' key
 *
 * The loader SILENT-DEGRADES to a disabled ({ enabled: false }) payload on any
 * error, so a missing/failing backend never breaks the host — the legacy team
 * dialog path is untouched by this module.
 *
 * THE GATE (the one thing that differs from coaching/places): team is "required but
 * flexible" — a participant must be on a team BEFORE the first `isTeamAdk:true`
 * activity. The server returns `gate.blocked` when that's unmet. We fold it into the
 * framework's `state` ('due' when blocked → the runner badges the chip + auto-opens
 * the panel to prompt join/create) and `rigidity` ('hard' when required && blocked,
 * so a REQUIRED unmet gate re-pops every load; 'soft' otherwise). This is the
 * "required before first isTeamAdk" gate made VISIBLE — a soft nudge + panel prompt,
 * NOT a hard route block (v1), matching the plan's flexible-required intent.
 *
 * The panel is the EXISTING ParticipantTeamDialog (which mounts the real m-team →
 * create/join/roster against the same ProgramTeam data). Mirrors useCoachingModule's
 * client-access pattern (useNexusClient() + the gql ops in
 * services/graphql/nexus/team.ts).
 */

import type { Component } from 'vue';
import { useNexusClient } from '@/composables/useApolloClients';
import { registerModuleDescriptor, type ModulePolicy } from '@/composables/useProgramModules';
import {
  TEAM_MODULE_STATE_QUERY,
  SET_PROGRAM_MODULE_MUTATION,
  type TeamModuleState,
  type TeamGate
} from '@/services/graphql/nexus/team';

// ============================================================
// Degraded defaults (silent-degrade targets)
// ============================================================

const DISABLED_GATE: TeamGate = {
  enabled: false,
  required: false,
  onTeam: false,
  firstTeamAdkIndex: null,
  firstTeamAdkName: null,
  reached: false,
  blocked: false,
  promptTiming: 'at-activity'
};

const DISABLED_MODULE_STATE: TeamModuleState = {
  enabled: false,
  gate: DISABLED_GATE
};

// ============================================================
// Pure mapper (no Apollo / no component context required)
// ============================================================

/**
 * Fold a TeamModuleState into the framework's open-ended ModulePolicy so the shared
 * trigger runner can read enabled/required/rigidity/inAppNudge PLUS a server-derived
 * `state` (nudge.deriveState is a pass-through of `state`).
 *
 * The gate → policy mapping (the load-bearing bit):
 *   - enabled  = gate.enabled
 *   - required = gate.required
 *   - state    = gate.blocked ? 'due' : 'idle'   (blocked = owe a join/create)
 *   - rigidity = gate.required && gate.blocked ? 'hard' : 'soft'
 *                (a REQUIRED unmet gate is 'hard' → the runner re-pops it every load
 *                 via isHardRequired; an unblocked or optional gate is 'soft')
 *   - inAppNudge = true (in-app is the gate's surface: the badge + the auto-open
 *                  prompt; SMS is out of scope for the gate v1)
 *
 * We ALSO fold the raw gate fields (onTeam / firstTeamAdkName / blocked / reached)
 * onto the policy so the panel can render the "you need a team to continue" copy
 * (ModulePolicy is open-ended: `[k: string]: unknown`).
 */
export function mapStateToPolicy(moduleState: TeamModuleState | null | undefined): ModulePolicy {
  const s = moduleState ?? DISABLED_MODULE_STATE;
  const gate = s.gate ?? DISABLED_GATE;
  const blocked = gate.blocked === true;
  const required = gate.required === true;
  // Capstone hard-gate envelope (read defensively — BE parallel change): an escape
  // hatch (allowSolo) or an explicit 'soft' mode DOWNGRADES the gate to a dismissible
  // nudge; the default ('hard') re-pops the panel every load so it can't be ignored.
  const allowSolo = gate.allowSolo === true;
  const softMode = gate.mode === 'soft';
  const hard = required && blocked && !allowSolo && !softMode;
  return {
    enabled: !!s.enabled && gate.enabled !== false,
    required,
    // A REQUIRED unmet HARD gate re-pops every load (isHardRequired); a solo-allowed
    // or soft-mode gate stays a dismissible nudge, matching the advance-path guard in
    // ParticipantGuide (teamHardBlockAt) so the two surfaces never disagree.
    rigidity: hard ? 'hard' : 'soft',
    // In-app is the gate's only surface (badge + auto-open prompt).
    inAppNudge: true,
    // Folded server-derived field (deriveState reads `state`): blocked → owed action.
    state: blocked ? 'due' : 'idle',
    // Raw gate fields carried through for the panel's blocked-affordance copy.
    onTeam: gate.onTeam,
    blocked,
    reached: gate.reached,
    firstTeamAdkIndex: gate.firstTeamAdkIndex,
    firstTeamAdkName: gate.firstTeamAdkName,
    // Envelope fields carried through so the panel/runner can reason about the gate.
    mode: gate.mode,
    allowSolo,
    // Auto-open timing (default 'at-activity'): the HOST (TimelineBar) reads this off
    // the policy to decide WHEN to let the runner auto-open the team panel — only
    // at/after firstTeamAdkIndex under 'at-activity', persistently under 'onboarding'.
    // Does NOT change `state`/`rigidity`, so the chip badge still reflects the owed
    // state and the hard advancement gate is untouched.
    promptTiming: gate.promptTiming ?? 'at-activity'
  };
}

// ============================================================
// Composable
// ============================================================

export function useTeamModule(providedClient?: ReturnType<typeof useNexusClient>) {
  // Accept an already-resolved client (async-context safe, mirrors usePlaces /
  // useCoachingModule): callers reached AFTER awaits (e.g. useAIPMOnboarding.getTeam)
  // pass the client they cached synchronously, since a fresh useNexusClient() there
  // throws "Apollo client with id nexus not found".
  const client = providedClient ?? useNexusClient();

  async function loadTeamModuleState(programId: string): Promise<TeamModuleState> {
    if (!programId) return DISABLED_MODULE_STATE;
    try {
      const { data } = await client.query({
        query: TEAM_MODULE_STATE_QUERY,
        variables: { programId },
        fetchPolicy: 'network-only'
      });
      return (data?.teamModuleState as TeamModuleState) ?? DISABLED_MODULE_STATE;
    } catch {
      // Silent-degrade: a failing/absent backend must never break the host.
      return DISABLED_MODULE_STATE;
    }
  }

  /**
   * The policyLoader the shared trigger runner iterates: loads the server module
   * state and maps the GATE into a ModulePolicy (with `state` folded in + rigidity
   * resolved from required && blocked).
   */
  async function loadPolicy(programId: string): Promise<ModulePolicy> {
    const moduleState = await loadTeamModuleState(programId);
    return mapStateToPolicy(moduleState);
  }

  /**
   * Flip the team gate's auto-open TIMING (Team Onboarding). Writes
   * AutoPilot.modules.team.promptTiming via the canonical setProgramModule writer:
   *   - 'onboarding'  → surface team formation as a soft onboarding phase (+ the legacy
   *                     persistent early auto-open)
   *   - 'at-activity' → only auto-open once the participant reaches the first isTeamAdk
   *
   * This ONLY moves the timing — it never touches the hard advancement gate
   * (mode/blocked). Silent-degrades like loadTeamModuleState: a failing/absent backend
   * must never break the settings save. Dark behind PROGRAM_MODULES + TEAM_ONBOARDING at
   * the call site.
   */
  async function setPromptTiming(
    programId: string,
    timing: 'onboarding' | 'at-activity'
  ): Promise<void> {
    if (!programId) return;
    try {
      await client.mutate({
        mutation: SET_PROGRAM_MODULE_MUTATION,
        // The knob lives at modules.team.CONFIG.promptTiming — setProgramModule merges
        // patch.config into the module's config (it ignores unknown top-level keys), and
        // teamGateState reads cfg.promptTiming. So the patch MUST be nested under `config`,
        // not sent as a bare top-level field (which would be silently dropped).
        variables: { programId, key: 'team', patch: { config: { promptTiming: timing } } }
      });
    } catch {
      // Silent-degrade: a failing/absent backend must never break the settings save.
    }
  }

  /**
   * Attach the Team module's capability hooks onto the 'team' descriptor key.
   * Additive + idempotent (registerModuleDescriptor is a replace), behind
   * PROGRAM_MODULES at the call site. Closes over THIS instance's loaders so the
   * policyLoader reuses the already-bound Nexus client. EXACTLY mirrors
   * useCoachingModule().registerDescriptor() — minus the onboardingPhase (deferred;
   * see the seam note below).
   *
   * ONBOARDING-PHASE SEAM (deferred, documented): a "join or create your team" phase
   * would reuse m-join-team / m-create-team, but those molecules require host
   * context the phase-list builder does not have (programDoc, teamList, teamDoc,
   * updateAdk — see their props). Rather than half-build a phase, the blocked gate is
   * surfaced through the PANEL (the runner auto-opens ParticipantTeamDialog, which
   * already carries the create/join UI) + the chip badge. This matches the deferred
   * onboarding phase-id wiring that Places + Coaching + Team ALL leave for a final
   * convergence pass.
   */
  function registerDescriptor(): void {
    registerModuleDescriptor('team', {
      // The host opens the team panel via onModuleSelect('team') → teamDialog = true;
      // the runner's auto-open routes through that same dispatch shim. Declaring the
      // panel marks 'team' a FULL module. Same ParticipantTeamDialog either path — the
      // host binds :program-doc (the panel needs it; the framework's generic mount
      // can't supply program context, so the open path stays the dispatch shim, EXACTLY
      // as 'coach' does with openCoachingPanel).
      panel: (() =>
        import(
          '@/components/organisms/community/ParticipantTeamDialog.vue'
        )) as unknown as () => Promise<Component>,
      policyLoader: loadPolicy,
      nudge: {
        // Pass-through: the gate → state fold runs in mapStateToPolicy; we surface it.
        deriveState: (policy: ModulePolicy) =>
          (policy.state as 'idle' | 'due' | 'overdue') ?? 'idle'
      }
    });
  }

  return {
    loadTeamModuleState,
    loadPolicy,
    setPromptTiming,
    registerDescriptor,
    mapStateToPolicy
  };
}

export default useTeamModule;
